import { useCallback, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { ClerkProvider, SignIn as ClerkSignInWidget, useAuth as useClerkAuth, useUser } from '@clerk/clerk-react';
import { AuthContext, type AuthValue } from './auth';

const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY as string | undefined;

/**
 * Clerk, behind the same interface as the development sign-in.
 *
 * Clerk authenticates and stops there (ADR-0013). Nothing below reads an
 * organization, a role, or a permission from Clerk — `/v1/me` answers all of
 * that from our own tables, because that is where authorization lives. The only
 * thing taken from Clerk is proof that a particular human is holding this
 * browser.
 */
export function ClerkAuthProvider({ children }: { children: ReactNode }) {
  if (!PUBLISHABLE_KEY) {
    // Loud and immediate. Clerk's own provider fails with a message about a
    // missing key that gives no hint which env var, in which file, for which
    // of two services.
    return (
      <div className="page">
        <div className="problem problem--error" role="alert">
          <h3>Clerk is not configured</h3>
          <p>
            <code>VITE_AUTH_PROVIDER=clerk</code> but no{' '}
            <code>CLERK_PUBLISHABLE_KEY</code> reached the browser. Set it in{' '}
            <code>.env</code> and rebuild, or set <code>AUTH_PROVIDER=local</code> to use the
            development sign-in. See <code>docs/setup-clerk.md</code>.
          </p>
        </div>
      </div>
    );
  }

  return (
    <ClerkProvider publishableKey={PUBLISHABLE_KEY} afterSignOutUrl="/">
      <ClerkBridge>{children}</ClerkBridge>
    </ClerkProvider>
  );
}

/**
 * Adapts Clerk's hooks to AuthValue.
 *
 * `getToken` delegates rather than caching. Clerk holds a short-lived token and
 * refreshes it behind the scenes; capturing one into state at sign-in would
 * hand the API a token that expires within the minute, and the symptom would be
 * intermittent 401s that look like an authorization bug.
 */
function ClerkBridge({ children }: { children: ReactNode }) {
  const { isLoaded, isSignedIn, getToken, signOut } = useClerkAuth();
  const { user } = useUser();
  // THE LAST CHOICE, PER ACCOUNT, IN THIS BROWSER (SUP-G01). Keyed by the
  // account, so one person's choice never applies to the next: a choice made in
  // this session counts only for the account that made it, and otherwise the
  // stored one is read. The shell re-checks it against their memberships and
  // drops one that no longer exists.
  const storageKey = user?.id ? `dc.membership:${user.id}` : null;
  const [choice, setChoice] = useState<{ key: string | null; id: string | null } | null>(null);
  const membershipId = choice && choice.key === storageKey
    ? choice.id
    : storageKey ? readMembership(storageKey) : null;
  const setMembership = useCallback((id: string | null) => {
    setChoice({ key: storageKey, id });
    if (storageKey) writeMembership(storageKey, id);
  }, [storageKey]);

  const value = useMemo<AuthValue>(
    () => ({
      // Not signed in until Clerk has loaded. Reporting `false` early would
      // render the sign-in screen for a moment to someone already signed in.
      isSignedIn: isLoaded && Boolean(isSignedIn),
      label: user?.primaryEmailAddress?.emailAddress ?? user?.id ?? null,
      getToken: async () => (isSignedIn ? await getToken() : null),
      signOut: () => void signOut(),
      membershipId,
      setMembership,
      pending: !isLoaded,
      error: null,
    }),
    [isLoaded, isSignedIn, getToken, signOut, user, membershipId, setMembership],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Clerk's hosted sign-in.
 *
 * Using Clerk's component rather than building a form: every credential the
 * user types stays inside it, so password handling, verification codes, OAuth
 * redirects and account recovery are never our code's problem — which is the
 * reason for buying authentication rather than writing it.
 */
export function ClerkSignIn() {
  return (
    <section className="signin">
      <header className="signin__head">
        <h2>Sign in</h2>
        <p className="muted">
          Clerk authenticates. Your school, role and caseload come from DreamCollege — signing in
          successfully is not the same as having access.
        </p>
      </header>
      <div className="signin__clerk">
        <ClerkSignInWidget routing="hash" />
      </div>
    </section>
  );
}

/** Storage can throw outright in a private window; the app works without it. */
function readMembership(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeMembership(key: string, id: string | null): void {
  try {
    if (id) localStorage.setItem(key, id);
    else localStorage.removeItem(key);
  } catch {
    /* non-fatal: the choice just will not survive a reload */
  }
}
